import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { makeStyles } from "@material-ui/core/styles";
import ClearIcon from "@mui/icons-material/Clear";

import FormBox from "../../component/FormBox/FormBox";
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    FormHelperText,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormLabel,
    Checkbox,
    FormGroup,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Typography,
} from "@material-ui/core";
import { isEmpty, isNotEmpty, isObjectEmpty } from "../../Utils";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentStep, setFieldValues } from "../../redux/action";
import { getFieldValuesByStep, getForm, getFormId } from "../../redux/selector";
import ErrorMsg from "../../component/ErrorMsg/ErrorMsg";
import CustomTextField from "../../inputComponent/CustomTextField/CustomTextField";
import CustomDatePicker from "../../component/CustomDatePicker/CustomDatePicker";
import FileInput from "../../component/FileInput/FileInput";
import moment from "moment";
import StepBar from "../../component/StepBar/StepBar";
import MultiColumnTable from "../../component/MultiColumnTable/MultiColumnTable";

export default function MultiColumnSample() {
    const formId = "Form050";
    const stepName = "Application";
    const maximumSignature = 2;
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    let defaultValues = useSelector(getFieldValuesByStep(stepName));

    const initialDefaultValues = {
        multiTable: [
            {
                field1: "",
                field2: "",
                field3: "",
                field4: "",
                field5: "",
                field6: "",
                field7: "",
                field8: "",
                field9: "",
            },
        ],
    };

    const testHeaders = [
        {
            name: "field1",
            type: "string",
        },
        {
            name: "field2",
            type: "string",
        },
        {
            name: "field3",
            type: "number",
        },
        {
            name: "field4",
            type: "number",
        },
        {
            name: "field5",
            type: "number",
        },
        {
            name: "field6",
            type: "number",
        },
        {
            name: "field7",
            type: "number",
        },
        {
            name: "field8",
            type: "number",
        },
        {
            name: "field9",
            type: "number",
        },
    ];

    if (isObjectEmpty(defaultValues)) {
        defaultValues = initialDefaultValues;
    }

    const yupSchema = yup.object().shape({
        multiTable: yup.array().of(
            yup.object().shape({
                field1: yup
                    .string()
                    .required(t(`errorMsg.required`))
                    .max(
                        60,
                        t(`errorMsg.maxTextLength`, {
                            max: 60,
                        })
                    )
                    .matches(
                        /^[a-zA-Z0-9\u4e00-\u9fa5,.\s'-]+$/,
                        t(`errorMsg.validWithName`, {
                            fieldName: t(`${formId}.label.location`),
                        })
                    ),

                field2: yup
                    .string()
                    .required(t(`errorMsg.required`))
                    .max(
                        60,
                        t(`errorMsg.maxTextLength`, {
                            max: 60,
                        })
                    )
                    .matches(
                        /^[a-zA-Z0-9\u4e00-\u9fa5,.\s'-]+$/,
                        t(`errorMsg.validWithName`, {
                            fieldName: t(`${formId}.label.location`),
                        })
                    ),

                field3: yup
                    .string()
                    .required(t("errorMsg.required"))
                    .test(
                        "minDigits",
                        t(`${formId}.errorMsg.amount.min`),
                        (value) => Number(value) >= 0
                    )
                    .test(
                        "maxDigits",
                        t(`${formId}.errorMsg.amount.max`),
                        (value) => Math.floor(Number(value)) <= 999
                    )
                    .test(
                        "maxDigitsAfterDecimal",
                        t(`${formId}.errorMsg.amount.maxDecimal`),
                        (number) => /(^$|^\d+(\.\d{1,3})?$)/.test(number)
                    ),

                field4: yup
                    .string()
                    .required(t("errorMsg.required"))
                    .test(
                        "minDigits",
                        t(`${formId}.errorMsg.amount.min`),
                        (value) => Number(value) >= 0
                    )
                    .test(
                        "maxDigits",
                        t(`${formId}.errorMsg.amount.max`),
                        (value) => Math.floor(Number(value)) <= 999
                    )
                    .test(
                        "maxDigitsAfterDecimal",
                        t(`${formId}.errorMsg.amount.maxDecimal`),
                        (number) => /(^$|^\d+(\.\d{1,3})?$)/.test(number)
                    ),

                field5: yup
                    .string()
                    .required(t("errorMsg.required"))
                    .test(
                        "minDigits",
                        t(`${formId}.errorMsg.amount.min`),
                        (value) => Number(value) >= 0
                    )
                    .test(
                        "maxDigits",
                        t(`${formId}.errorMsg.amount.max`),
                        (value) => Math.floor(Number(value)) <= 999
                    )
                    .test(
                        "maxDigitsAfterDecimal",
                        t(`${formId}.errorMsg.amount.maxDecimal`),
                        (number) => /(^$|^\d+(\.\d{1,3})?$)/.test(number)
                    ),

                field6: yup
                    .string()
                    .required(t("errorMsg.required"))
                    .test(
                        "minDigits",
                        t(`${formId}.errorMsg.amount.min`),
                        (value) => Number(value) >= 0
                    )
                    .test(
                        "maxDigits",
                        t(`${formId}.errorMsg.amount.max`),
                        (value) => Math.floor(Number(value)) <= 999
                    )
                    .test(
                        "maxDigitsAfterDecimal",
                        t(`${formId}.errorMsg.amount.maxDecimal`),
                        (number) => /(^$|^\d+(\.\d{1,3})?$)/.test(number)
                    ),

                field7: yup
                    .string()
                    .required(t("errorMsg.required"))
                    .test(
                        "minDigits",
                        t(`${formId}.errorMsg.amount.min`),
                        (value) => Number(value) >= 0
                    )
                    .test(
                        "maxDigits",
                        t(`${formId}.errorMsg.amount.max`),
                        (value) => Math.floor(Number(value)) <= 999
                    )
                    .test(
                        "maxDigitsAfterDecimal",
                        t(`${formId}.errorMsg.amount.maxDecimal`),
                        (number) => /(^$|^\d+(\.\d{1,3})?$)/.test(number)
                    ),

                field8: yup
                    .string()
                    .required(t("errorMsg.required"))
                    .test(
                        "minDigits",
                        t(`${formId}.errorMsg.amount.min`),
                        (value) => Number(value) >= 0
                    )
                    .test(
                        "maxDigits",
                        t(`${formId}.errorMsg.amount.max`),
                        (value) => Math.floor(Number(value)) <= 999
                    )
                    .test(
                        "maxDigitsAfterDecimal",
                        t(`${formId}.errorMsg.amount.maxDecimal`),
                        (number) => /(^$|^\d+(\.\d{1,3})?$)/.test(number)
                    ),

                field9: yup
                    .string()
                    .required(t("errorMsg.required"))
                    .test(
                        "minDigits",
                        t(`${formId}.errorMsg.amount.min`),
                        (value) => Number(value) >= 0
                    )
                    .test(
                        "maxDigits",
                        t(`${formId}.errorMsg.amount.max`),
                        (value) => Math.floor(Number(value)) <= 999
                    )
                    .test(
                        "maxDigitsAfterDecimal",
                        t(`${formId}.errorMsg.amount.maxDecimal`),
                        (number) => /(^$|^\d+(\.\d{1,3})?$)/.test(number)
                    ),
            })
        ),
    });

    const {
        getValues,
        register,
        handleSubmit,
        control,
        formState,
        reset,
        watch,
        setValue,
        clearErrors,
    } = useForm({
        defaultValues,
        mode: "onBlur",
        resolver: yupResolver(yupSchema),
    });

    const onStepSubmit = (formData) => {
        dispatch(setFieldValues(stepName, formData));
        dispatch(setCurrentStep("Confirmation"));
        console.log("onstep");
    };

    const onStepError = (errors) => {
        //onStepError
        console.log("onStepError, errors: ", errors);
    };

    const onPrev = () => {
        dispatch(setCurrentStep("Note"));
    };

    const onReset = () => {
        reset(initialDefaultValues);
    };

    const multiTableRecord = useFieldArray({
        control,
        name: "multiTable",
    });

    const handleAppend = () => {
        multiTableRecord.append({
            field1: "",
            field2: "",
            field3: "",
            field4: "",
            field5: "",
            field6: "",
            field7: "",
            field8: "",
            field9: "",
        });
    };

    const handleRemove = (index) => {
        multiTableRecord.remove(index);
    };

    console.log("errors: ", formState.errors);
    //
    console.log("get values: ", getValues());
    const errors = formState.errors;

    return (
        <Box>
            <form onSubmit={handleSubmit(onStepSubmit, onStepError)} noValidate>
                <FormBox header={"Sample Multiple Column Table"}>
                    <MultiColumnTable
                        label="simple table"
                        numOfColumnPerPage={4}
                        headers={testHeaders}
                        formId={formId}
                        errors={errors}
                        records={multiTableRecord.fields}
                        control={control}
                        handleAppend={handleAppend}
                        handleRemove={handleRemove}
                    />
                </FormBox>

                <StepBar
                    onBack={onPrev}
                    onClear={onReset}
                    onNext={onStepSubmit}
                    handleSubmit={handleSubmit}
                    onError={onStepError}
                />
            </form>
        </Box>
    );
}
